import { makeAuthenticatedRequest } from './authService';
import { aiCharacterService, AICharacter } from './aiCharacterService';
import { NovelService } from './novelService';
import { cashflowService } from './cashflowService';
import { taskService } from './taskService';
import { habitService } from './habitService';
import { goalService } from './goalService';
import { KnowledgeCategoryService } from './knowledgeCategoryService';
import { NotesService } from './notesService';

// ========================================
// Types for Generic Chat Service
// ========================================

export interface ChatConfig {
  characterId?: number; // If using AI character
  provider?: 'claude' | 'gemini' | 'gpt' | 'grok'; // If using default
  model?: string; // Model name
}

export interface ToolCall {
  name: string;
  input: any;
  result?: any;
  isError?: boolean;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  images?: Array<{
    data: string; // base64 data
    mimeType: string;
    alt?: string;
  }>;
  toolCalls?: ToolCall[];
}

export interface ChatResponse {
  response: string;
  content: any[];
  stop_reason: string;
  usage?: {
    input_tokens?: number;
    output_tokens?: number;
  };
  model: string;
  id?: string;
  images?: Array<{
    data: string;
    mimeType: string;
  }>;
  toolCalls?: ToolCall[];
}

export interface ToolUseBlock {
  type: 'tool_use';
  id: string;
  name: string;
  input: any;
}

export interface ToolResultBlock {
  type: 'tool_result';
  tool_use_id: string;
  content: string | any[];
  is_error?: boolean;
}

export interface ChatSession {
  id: number;
  user_id: number;
  title: string | null;
  character_id: number | null;
  provider: 'claude' | 'gemini' | 'gpt' | 'grok' | null;
  model: string | null;
  created_at: string;
  updated_at: string;
  last_user_message_at?: string | null;
  first_message_preview?: string;
}

// Default models for each provider
const DEFAULT_MODELS = {
  claude: 'claude-haiku-4-5',
  gemini: 'gemini-2.5-pro',
  gpt: 'gpt-5', // Placeholder, not yet implemented
  grok: 'grok-4.6'
};

// Default system prompts for each provider
const DEFAULT_SYSTEM_PROMPTS = {
  claude: 'You are a helpful, harmless, and honest AI assistant. You provide clear, accurate, and thoughtful responses to help users with their questions and tasks.',
  gemini: 'You are a helpful, harmless, and honest AI assistant. You provide clear, accurate, and thoughtful responses to help users with their questions and tasks.',
  gpt: 'You are a helpful, harmless, and honest AI assistant. You provide clear, accurate, and thoughtful responses to help users with their questions and tasks.',
  grok: 'You are a helpful, harmless, and honest AI assistant. You provide clear, accurate, and thoughtful responses to help users with their questions and tasks.'
};

// Helper function to make authenticated API calls
// Uses makeAuthenticatedRequest from authService for consistent authentication handling
const apiCall = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  try {
    return await makeAuthenticatedRequest(endpoint, options);
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
};

// ========================================
// Generic Chat Service Class
// ========================================

export class GenericChatService {
  /**
   * Create a new chat session
   */
  static async createSession(config: ChatConfig, title?: string): Promise<ChatSession> {
    try {
      const body: any = {};
      
      if (config.characterId) {
        body.character_id = config.characterId;
      } else {
        const provider = config.provider || 'claude';
        body.provider = provider;
        body.model = config.model || DEFAULT_MODELS[provider as keyof typeof DEFAULT_MODELS];
      }
      
      if (title) {
        body.title = title;
      }
      
      const response = await apiCall('/chat/sessions', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to create session');
      }
      
      return response.session;
    } catch (error) {
      console.error('Failed to create chat session:', error);
      throw error;
    }
  }

  /**
   * Load all chat sessions for a user
   */
  static async loadSessions(userId: number): Promise<ChatSession[]> {
    try {
      const response = await apiCall(`/chat/sessions?user_id=${userId}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to load sessions');
      }
      
      return response.sessions || [];
    } catch (error) {
      console.error('Failed to load chat sessions:', error);
      throw error;
    }
  }

  /**
   * Load a specific chat session with all messages
   */
  static async loadSession(sessionId: number): Promise<{ session: ChatSession; messages: ChatMessage[] }> {
    try {
      const response = await apiCall(`/chat/sessions/${sessionId}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to load session');
      }
      
      // Convert database messages to ChatMessage format
      const messages: ChatMessage[] = (response.messages || []).map((msg: any) => ({
        id: msg.id.toString(),
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp),
        images: msg.images || undefined,
        toolCalls: msg.tool_calls || undefined
      }));
      
      return {
        session: response.session,
        messages
      };
    } catch (error) {
      console.error('Failed to load chat session:', error);
      throw error;
    }
  }

  /**
   * Update session title
   */
  static async updateSessionTitle(sessionId: number, title: string): Promise<ChatSession> {
    try {
      const response = await apiCall(`/chat/sessions/${sessionId}`, {
        method: 'PUT',
        body: JSON.stringify({ title })
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update session');
      }
      
      return response.session;
    } catch (error) {
      console.error('Failed to update session title:', error);
      throw error;
    }
  }

  /**
   * Delete a chat session
   */
  static async deleteSession(sessionId: number): Promise<void> {
    try {
      const response = await apiCall(`/chat/sessions/${sessionId}`, {
        method: 'DELETE'
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete session');
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
      throw error;
    }
  }

  /**
   * Save a message to a session
   */
  static async saveMessage(sessionId: number, message: ChatMessage): Promise<void> {
    try {
      await apiCall(`/chat/sessions/${sessionId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          role: message.role,
          content: message.content,
          images: message.images || null,
          tool_calls: message.toolCalls || null,
          timestamp: message.timestamp.toISOString()
        })
      });
    } catch (error) {
      console.error('Failed to save message:', error);
      throw error;
    }
  }

  /**
   * Save chat history (creates session if needed)
   * Note: This method saves messages individually. For bulk saving, use saveMessage for each message.
   */
  static async saveChatHistory(config: ChatConfig, messages: ChatMessage[], sessionId?: number): Promise<number> {
    try {
      // If no sessionId, create a new session
      if (!sessionId) {
        const session = await this.createSession(config);
        sessionId = session.id;
      }
      
      // Save all messages (only user and assistant messages, skip welcome messages)
      // Note: Messages should already be saved individually when sent, this is mainly for ensuring consistency
      for (const message of messages) {
        if (message.role === 'user' || message.role === 'assistant') {
          // Skip welcome messages
          if (message.role === 'assistant' && message.content.includes('Hello')) {
            continue;
          }
          await this.saveMessage(sessionId, message);
        }
      }
      
      return sessionId;
    } catch (error) {
      console.error('Failed to save chat history:', error);
      throw error;
    }
  }

  /**
   * Load chat history from a session
   */
  static async loadChatHistory(config: ChatConfig, sessionId?: number): Promise<ChatMessage[]> {
    if (!sessionId) {
      return [];
    }
    
    try {
      const { messages } = await this.loadSession(sessionId);
      return messages;
    } catch (error) {
      console.error('Failed to load chat history:', error);
      return [];
    }
  }

  /**
   * Create a chat message
   */
  static createMessage(content: string, role: 'user' | 'assistant', images?: ChatMessage['images'], toolCalls?: ToolCall[]): ChatMessage {
    return {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      role,
      content,
      timestamp: new Date(),
      images,
      toolCalls
    };
  }

  /**
   * Tool registry - maps tool names to tool definitions
   */
  static readonly TOOL_REGISTRY: Record<string, any> = {
    'get_user_novels': {
      name: 'get_user_novels',
      description: 'Get a list of novels for the current user. Use this when the user asks about their novels, wants to see what novels they have, or when you need to understand their writing projects to provide suggestions.',
      input_schema: {
        type: 'object',
        properties: {
          limit: {
            type: 'number',
            description: 'Maximum number of novels to return. Default is all novels.'
          },
          sort_order: {
            type: 'string',
            enum: ['ASC', 'DESC'],
            description: 'Sort order by creation date. ASC for oldest first, DESC for newest first. Default is DESC.'
          }
        }
      }
    },
    'get_novel': {
      name: 'get_novel',
      description: 'Get detailed information about a specific novel including its metadata, chapters, and content. Use this when the user asks about a specific novel or when you need detailed information to provide suggestions.',
      input_schema: {
        type: 'object',
        properties: {
          novel_id: {
            type: 'string',
            description: 'The novel_id (string identifier) of the novel to retrieve.'
          }
        },
        required: ['novel_id']
      }
    },
    'get_chapter': {
      name: 'get_chapter',
      description: 'Get the content of a specific chapter from a novel. Use this when the user asks about a specific chapter or when you need chapter content to provide writing suggestions.',
      input_schema: {
        type: 'object',
        properties: {
          novel_id: {
            type: 'string',
            description: 'The novel_id (string identifier) of the novel.'
          },
          chapter_id: {
            type: 'string',
            description: 'The chapter_id (string identifier) of the chapter to retrieve.'
          }
        },
        required: ['novel_id', 'chapter_id']
      }
    },
    'update_novel': {
      name: 'update_novel',
      description: 'Update a novel\'s title and status. Use this when the user asks to change a novel\'s title or update its status (on-going, pending, completed).',
      input_schema: {
        type: 'object',
        properties: {
          novel_id: {
            type: 'string',
            description: 'The novel_id (string identifier) of the novel to update.'
          },
          title: {
            type: 'string',
            description: 'The new title for the novel.'
          },
          status: {
            type: 'string',
            enum: ['on-going', 'pending', 'completed'],
            description: 'The new status for the novel. Optional.'
          }
        },
        required: ['novel_id', 'title']
      }
    },
    'update_novel_metadata': {
      name: 'update_novel_metadata',
      description: 'Update a novel\'s metadata including genre, plots, characters, settings, themes, writing style, target audience, language, and notes. Use this when the user asks to update any metadata information about their novel.',
      input_schema: {
        type: 'object',
        properties: {
          novel_id: {
            type: 'string',
            description: 'The novel_id (string identifier) of the novel to update.'
          },
          genre: {
            type: 'string',
            description: 'The genre of the novel. Optional.'
          },
          one_paragraph_summary: {
            type: 'string',
            description: 'A one-paragraph summary of the novel. Optional.'
          },
          plots: {
            type: 'array',
            items: {
              type: 'string'
            },
            description: 'Array of plot descriptions. Optional.'
          },
          characters: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                name: {
                  type: 'string',
                  description: 'Character name.'
                },
                description: {
                  type: 'string',
                  description: 'Character description.'
                },
                role: {
                  type: 'string',
                  enum: ['protagonist', 'antagonist', 'supporting', 'other'],
                  description: 'Character role.'
                }
              },
              required: ['name']
            },
            description: 'Array of character objects. Optional.'
          },
          settings: {
            type: 'array',
            items: {
              type: 'string'
            },
            description: 'Array of setting descriptions. Optional.'
          },
          themes: {
            type: 'array',
            items: {
              type: 'string'
            },
            description: 'Array of theme descriptions. Optional.'
          },
          writing_style: {
            type: 'string',
            description: 'Description of the writing style. Optional.'
          },
          target_audience: {
            type: 'string',
            description: 'Description of the target audience. Optional.'
          },
          language: {
            type: 'string',
            description: 'The language of the novel. Optional.'
          },
          notes: {
            type: 'string',
            description: 'Additional notes about the novel. Optional.'
          }
        },
        required: ['novel_id']
      }
    },
    'get_cashflow_entries': {
      name: 'get_cashflow_entries',
      description: 'Get cashflow entries for the user. Use this when the user asks about their financial transactions, spending, income, or wants to see their cashflow history.',
      input_schema: {
        type: 'object',
        properties: {
          start_date: {
            type: 'string',
            description: 'Start date for filtering entries (YYYY-MM-DD format).'
          },
          end_date: {
            type: 'string',
            description: 'End date for filtering entries (YYYY-MM-DD format).'
          },
          category: {
            type: 'string',
            description: 'Filter by category name.'
          },
          exclude_categories: {
            type: 'string',
            description: 'Comma-separated list of categories to exclude.'
          },
          limit: {
            type: 'number',
            description: 'Maximum number of entries to return. Default is all entries.'
          },
          offset: {
            type: 'number',
            description: 'Number of entries to skip for pagination.'
          }
        }
      }
    },
    'get_cashflow_summary': {
      name: 'get_cashflow_summary',
      description: 'Get cashflow summary statistics (spent, income, or investment) by period. Use this when the user asks about their spending summary, income summary, or investment summary.',
      input_schema: {
        type: 'object',
        properties: {
          type: {
            type: 'string',
            enum: ['spent', 'income', 'investment'],
            description: 'Type of summary: spent, income, or investment.'
          },
          period: {
            type: 'string',
            enum: ['month', 'year'],
            description: 'Period for summary: month or year. Default is month.'
          }
        },
        required: ['type']
      }
    },
    'get_cashflow_categories': {
      name: 'get_cashflow_categories',
      description: 'Get all available cashflow categories. Use this when the user asks about what categories they have used or wants to see available categories.',
      input_schema: {
        type: 'object',
        properties: {}
      }
    },
    'get_user_tasks': {
      name: 'get_user_tasks',
      description: 'Get tasks for the user with optional filters. Use this when the user asks about their tasks, wants to see their todo list, or needs information about their tasks.',
      input_schema: {
        type: 'object',
        properties: {
          status: {
            type: 'string',
            enum: ['todo', 'in_progress', 'completed', 'on_hold', 'cancelled'],
            description: 'Filter by task status.'
          },
          due_date: {
            type: 'string',
            description: 'Filter by due date (YYYY-MM-DD format).'
          },
          assigned_date: {
            type: 'string',
            description: 'Filter by assigned date (YYYY-MM-DD format).'
          },
          limit: {
            type: 'number',
            description: 'Maximum number of tasks to return. Default is all tasks.'
          },
          offset: {
            type: 'number',
            description: 'Number of tasks to skip for pagination.'
          }
        }
      }
    },
    'get_task_by_id': {
      name: 'get_task_by_id',
      description: 'Get detailed information about a specific task. Use this when the user asks about a specific task or needs detailed task information.',
      input_schema: {
        type: 'object',
        properties: {
          task_id: {
            type: 'string',
            description: 'The task ID to retrieve.'
          }
        },
        required: ['task_id']
      }
    },
    'get_tasks_due_today': {
      name: 'get_tasks_due_today',
      description: 'Get tasks that are due today. Use this when the user asks about today\'s tasks or what they need to do today.',
      input_schema: {
        type: 'object',
        properties: {
          status: {
            type: 'string',
            enum: ['todo', 'in_progress', 'completed', 'on_hold', 'cancelled'],
            description: 'Filter by task status. If not provided, returns all statuses.'
          }
        }
      }
    },
    'get_tasks_assigned_for_date': {
      name: 'get_tasks_assigned_for_date',
      description: 'Get tasks assigned for a specific date. Use this when the user asks about tasks for a particular date.',
      input_schema: {
        type: 'object',
        properties: {
          assigned_date: {
            type: 'string',
            description: 'The assigned date to filter by (YYYY-MM-DD format).'
          },
          status: {
            type: 'string',
            enum: ['todo', 'in_progress', 'completed', 'on_hold', 'cancelled'],
            description: 'Filter by task status. If not provided, returns all statuses.'
          }
        },
        required: ['assigned_date']
      }
    },
    'get_task_stats': {
      name: 'get_task_stats',
      description: 'Get task statistics including counts by status, overdue tasks, and tasks due today. Use this when the user asks about their task statistics or wants an overview of their tasks.',
      input_schema: {
        type: 'object',
        properties: {}
      }
    },
    'get_user_habits': {
      name: 'get_user_habits',
      description: 'Get habits for the user with optional filters. Use this when the user asks about their habits, wants to see their habit list, or needs information about their habits.',
      input_schema: {
        type: 'object',
        properties: {
          category: {
            type: 'string',
            description: 'Filter by habit category.'
          },
          is_active: {
            type: 'boolean',
            description: 'Filter by active status. If true, returns only active habits. If false, returns only inactive habits.'
          },
          limit: {
            type: 'number',
            description: 'Maximum number of habits to return. Default is all habits.'
          },
          offset: {
            type: 'number',
            description: 'Number of habits to skip for pagination.'
          }
        }
      }
    },
    'get_habit_by_id': {
      name: 'get_habit_by_id',
      description: 'Get detailed information about a specific habit. Use this when the user asks about a specific habit or needs detailed habit information.',
      input_schema: {
        type: 'object',
        properties: {
          habit_id: {
            type: 'number',
            description: 'The habit ID to retrieve.'
          }
        },
        required: ['habit_id']
      }
    },
    'get_daily_completions': {
      name: 'get_daily_completions',
      description: 'Get habit completions for a specific date. Use this when the user asks about their habit completions for a particular day.',
      input_schema: {
        type: 'object',
        properties: {
          date: {
            type: 'string',
            description: 'The date to get completions for (YYYY-MM-DD format).'
          }
        },
        required: ['date']
      }
    },
    'get_habit_stats': {
      name: 'get_habit_stats',
      description: 'Get habit completion statistics. Use this when the user asks about their habit statistics, success rates, or wants an overview of their habit performance.',
      input_schema: {
        type: 'object',
        properties: {
          habit_id: {
            type: 'number',
            description: 'Optional habit ID to get stats for a specific habit. If not provided, returns stats for all habits.'
          },
          start_date: {
            type: 'string',
            description: 'Start date for statistics (YYYY-MM-DD format).'
          },
          end_date: {
            type: 'string',
            description: 'End date for statistics (YYYY-MM-DD format).'
          },
          days: {
            type: 'number',
            description: 'Number of days to look back from today for statistics.'
          }
        }
      }
    },
    'get_habit_streak': {
      name: 'get_habit_streak',
      description: 'Get streak information for a specific habit including current streak, longest streak, and success rate. Use this when the user asks about their habit streaks.',
      input_schema: {
        type: 'object',
        properties: {
          habit_id: {
            type: 'number',
            description: 'The habit ID to get streak information for.'
          }
        },
        required: ['habit_id']
      }
    },
    'get_user_goals': {
      name: 'get_user_goals',
      description: 'Get goals for the user with optional filters. Use this when the user asks about their goals, wants to see their goal list, or needs information about their goals.',
      input_schema: {
        type: 'object',
        properties: {
          status: {
            type: 'string',
            description: 'Filter by goal status (e.g., active, completed, paused).'
          },
          category: {
            type: 'string',
            description: 'Filter by goal category.'
          },
          limit: {
            type: 'number',
            description: 'Maximum number of goals to return. Default is all goals.'
          },
          offset: {
            type: 'number',
            description: 'Number of goals to skip for pagination.'
          }
        }
      }
    },
    'get_goal_by_id': {
      name: 'get_goal_by_id',
      description: 'Get detailed information about a specific goal. Use this when the user asks about a specific goal or needs detailed goal information.',
      input_schema: {
        type: 'object',
        properties: {
          goal_id: {
            type: 'string',
            description: 'The goal ID to retrieve.'
          }
        },
        required: ['goal_id']
      }
    },
    'get_goal_progress_history': {
      name: 'get_goal_progress_history',
      description: 'Get progress history for a specific goal showing how progress has changed over time. Use this when the user asks about goal progress history or wants to see progress trends.',
      input_schema: {
        type: 'object',
        properties: {
          goal_id: {
            type: 'string',
            description: 'The goal ID to get progress history for.'
          },
          limit: {
            type: 'number',
            description: 'Maximum number of history entries to return.'
          },
          start_date: {
            type: 'string',
            description: 'Start date for filtering history (YYYY-MM-DD format).'
          },
          end_date: {
            type: 'string',
            description: 'End date for filtering history (YYYY-MM-DD format).'
          }
        },
        required: ['goal_id']
      }
    },
    'create_task': {
      name: 'create_task',
      description: 'Create a new task for the user. Use this when the user asks you to create a task, add something to their todo list, or create a reminder.',
      input_schema: {
        type: 'object',
        properties: {
          title: {
            type: 'string',
            description: 'The title of the task (required).'
          },
          description: {
            type: 'string',
            description: 'Optional description or details about the task.'
          },
          tag_names: {
            type: 'array',
            items: {
              type: 'string'
            },
            description: 'Optional array of tag names to associate with the task.'
          }
        },
        required: ['title']
      }
    },
    'get_knowledge_categories': {
      name: 'get_knowledge_categories',
      description: 'Get a list of all knowledge categories for the user. Use this when the user asks about their learning categories, knowledge areas, or wants to see what categories they have.',
      input_schema: {
        type: 'object',
        properties: {
          limit: {
            type: 'number',
            description: 'Maximum number of categories to return. Default is all categories.'
          },
          offset: {
            type: 'number',
            description: 'Number of categories to skip for pagination.'
          }
        }
      }
    },
    'get_knowledge_category': {
      name: 'get_knowledge_category',
      description: 'Get detailed information about a specific knowledge category including its prompt template and lesson structure. Use this when the user asks about a specific category or needs detailed category information.',
      input_schema: {
        type: 'object',
        properties: {
          category_id: {
            type: 'string',
            description: 'The category_id (string identifier) of the category to retrieve.'
          }
        },
        required: ['category_id']
      }
    },
    'get_lessons': {
      name: 'get_lessons',
      description: 'Get lessons for a specific knowledge category. Use this when the user asks about lessons in a category, wants to see what they have learned, or needs to review past lessons.',
      input_schema: {
        type: 'object',
        properties: {
          category_slug: {
            type: 'string',
            description: 'The category_slug (string identifier) of the category to get lessons for.'
          },
          limit: {
            type: 'number',
            description: 'Maximum number of lessons to return. Default is 50.'
          },
          offset: {
            type: 'number',
            description: 'Number of lessons to skip for pagination.'
          }
        },
        required: ['category_slug']
      }
    },
    'get_lesson': {
      name: 'get_lesson',
      description: 'Get detailed information about a specific lesson including its full content. Use this when the user asks about a specific lesson or needs to review lesson content.',
      input_schema: {
        type: 'object',
        properties: {
          lesson_id: {
            type: 'string',
            description: 'The lesson_id (string identifier) of the lesson to retrieve.'
          }
        },
        required: ['lesson_id']
      }
    },
    'get_daily_notes': {
      name: 'get_daily_notes',
      description: 'Get notes for a specific date with their full content (blocks). Use this when the user asks about their notes for a particular day, wants to review what they wrote on a date, or needs to see their daily notes content.',
      input_schema: {
        type: 'object',
        properties: {
          date: {
            type: 'string',
            description: 'The date to get notes for (YYYY-MM-DD format).'
          },
          limit: {
            type: 'number',
            description: 'Maximum number of notes to return. Default is all notes for the date.'
          }
        },
        required: ['date']
      }
    }
  };

  /**
   * Get all novel tools for Claude function calling
   */
  static getNovelTools(): any[] {
    return Object.values(this.TOOL_REGISTRY);
  }

  /**
   * Get tools for a character based on enabled tool names
   * @param enabledToolNames Array of tool names that are enabled for the character
   * @returns Array of tool definitions, or undefined if no tools enabled
   */
  static getToolsForCharacter(enabledToolNames?: string[]): any[] | undefined {
    if (!enabledToolNames || enabledToolNames.length === 0) {
      return undefined;
    }

    const tools = enabledToolNames
      .map(toolName => this.TOOL_REGISTRY[toolName])
      .filter(tool => tool !== undefined); // Filter out any invalid tool names

    return tools.length > 0 ? tools : undefined;
  }

  /**
   * Execute a tool call
   */
  static async executeTool(toolName: string, args: any, userId: number): Promise<any> {
    try {
      switch (toolName) {
        case 'get_user_novels': {
          const novels = await NovelService.getUserNovels(userId, {
            limit: args.limit,
            sort_order: args.sort_order as 'ASC' | 'DESC' | undefined
          });
          return {
            success: true,
            data: novels.map(novel => ({
              id: novel.id,
              novel_id: novel.novel_id,
              title: novel.title,
              status: novel.status,
              genre: novel.genre,
              created_at: novel.created_at,
              updated_at: novel.updated_at,
              chapters_count: novel.chapters?.length || 0
            }))
          };
        }

        case 'get_novel': {
          const novel = await NovelService.getNovel(args.novel_id);
          return {
            success: true,
            data: {
              id: novel.id,
              novel_id: novel.novel_id,
              title: novel.title,
              status: novel.status,
              genre: novel.genre,
              metadata: novel.metadata,
              chapters: novel.chapters.map(ch => ({
                chapter_id: ch.chapter_id,
                title: ch.title,
                order: ch.order,
                has_content: !!ch.content
              })),
              created_at: novel.created_at,
              updated_at: novel.updated_at
            }
          };
        }

        case 'get_chapter': {
          const chapter = await NovelService.getChapter(args.novel_id, args.chapter_id);
          return {
            success: true,
            data: {
              chapter_id: chapter.chapter_id,
              title: chapter.title,
              order: chapter.order,
              content: chapter.content?.text_value || null,
              created_at: chapter.content?.created_at || null,
              updated_at: chapter.content?.updated_at || null
            }
          };
        }

        case 'update_novel': {
          const novel = await NovelService.updateNovel(args.novel_id, {
            title: args.title,
            status: args.status
          });
          return {
            success: true,
            data: {
              novel_id: novel.novel_id,
              title: novel.title,
              status: novel.status,
              updated_at: novel.updated_at
            }
          };
        }

        case 'update_novel_metadata': {
          // Build metadata object from provided fields
          const metadata: any = {};
          if (args.one_paragraph_summary !== undefined) {
            metadata.oneParagraphSummary = args.one_paragraph_summary;
          }
          if (args.plots !== undefined) {
            metadata.plots = args.plots;
          }
          if (args.characters !== undefined) {
            metadata.characters = args.characters;
          }
          if (args.settings !== undefined) {
            metadata.settings = args.settings;
          }
          if (args.themes !== undefined) {
            metadata.themes = args.themes;
          }
          if (args.writing_style !== undefined) {
            metadata.writingStyle = args.writing_style;
          }
          if (args.target_audience !== undefined) {
            metadata.targetAudience = args.target_audience;
          }
          if (args.language !== undefined) {
            metadata.language = args.language;
          }
          if (args.notes !== undefined) {
            metadata.notes = args.notes;
          }

          const novel = await NovelService.updateNovelMetadata(
            args.novel_id,
            args.genre,
            Object.keys(metadata).length > 0 ? metadata : undefined
          );
          return {
            success: true,
            data: {
              novel_id: novel.novel_id,
              title: novel.title,
              genre: novel.genre,
              metadata: novel.metadata,
              updated_at: novel.updated_at
            }
          };
        }

        case 'get_cashflow_entries': {
          const response = await cashflowService.getCashflowEntries({
            start_date: args.start_date,
            end_date: args.end_date,
            category: args.category,
            exclude_categories: args.exclude_categories,
            limit: args.limit,
            offset: args.offset
          });
          return {
            success: response.success,
            data: response.cashflow || [],
            count: response.count
          };
        }

        case 'get_cashflow_summary': {
          const response = await cashflowService.getCashflowSummary(
            args.type || 'spent',
            args.period || 'month'
          );
          return {
            success: response.success,
            data: {
              type: response.type,
              period: response.period,
              summary: response.summary || []
            }
          };
        }

        case 'get_cashflow_categories': {
          const response = await cashflowService.getCategories();
          return {
            success: response.success,
            data: response.categories || []
          };
        }

        case 'get_user_tasks': {
          const response = await taskService.getUserTasks({
            user_id: userId.toString(),
            status: args.status,
            due_date: args.due_date,
            assigned_date: args.assigned_date,
            limit: args.limit,
            offset: args.offset
          });
          return {
            success: response.success,
            data: response.tasks || [],
            count: response.count
          };
        }

        case 'get_task_by_id': {
          const response = await taskService.getTaskById(args.task_id);
          return {
            success: response.success,
            data: response.task || null
          };
        }

        case 'get_tasks_due_today': {
          const response = await taskService.getTasksDueToday(userId.toString(), args.status);
          return {
            success: response.success,
            data: response.tasks || [],
            count: response.count
          };
        }

        case 'get_tasks_assigned_for_date': {
          const response = await taskService.getTasksAssignedForDate(
            userId.toString(),
            args.assigned_date,
            args.status
          );
          return {
            success: response.success,
            data: response.tasks || [],
            count: response.count
          };
        }

        case 'get_task_stats': {
          const response = await taskService.getTaskStats(userId.toString());
          return {
            success: response.success,
            data: response.stats || null
          };
        }

        case 'get_user_habits': {
          const response = await habitService.getUserHabits(userId, {
            category: args.category,
            is_active: args.is_active,
            limit: args.limit,
            offset: args.offset
          });
          return {
            success: response.success,
            data: response.habits || [],
            count: response.count
          };
        }

        case 'get_habit_by_id': {
          const response = await habitService.getHabitById(args.habit_id);
          return {
            success: response.success,
            data: response.habit || null
          };
        }

        case 'get_daily_completions': {
          const response = await habitService.getDailyCompletions(userId, args.date);
          return {
            success: response.success,
            data: {
              date: response.date || args.date,
              completions: response.completions || [],
              count: response.count || 0
            }
          };
        }

        case 'get_habit_stats': {
          const response = await habitService.getCompletionStats(
            userId,
            args.habit_id,
            args.start_date,
            args.end_date,
            args.days
          );
          return {
            success: response.success,
            data: response.stats || null
          };
        }

        case 'get_habit_streak': {
          const response = await habitService.getHabitStreak(args.habit_id, userId);
          return {
            success: response.success,
            data: response.streak || null
          };
        }

        case 'get_user_goals': {
          const response = await goalService.getUserGoals({
            user_id: userId.toString(),
            status: args.status,
            category: args.category,
            limit: args.limit,
            offset: args.offset
          });
          return {
            success: response.success,
            data: response.goals || [],
            count: response.count
          };
        }

        case 'get_goal_by_id': {
          const response = await goalService.getGoalById(args.goal_id);
          return {
            success: response.success,
            data: response.goal || null
          };
        }

        case 'get_goal_progress_history': {
          const response = await goalService.getGoalProgressHistory(args.goal_id, {
            limit: args.limit,
            startDate: args.start_date,
            endDate: args.end_date
          });
          return {
            success: response.success,
            data: response.history || []
          };
        }

        case 'create_task': {
          const response = await taskService.createTask(userId.toString(), {
            title: args.title,
            description: args.description,
            tag_names: args.tag_names
          });
          return {
            success: response.success,
            data: {
              id: response.id,
              message: response.message
            }
          };
        }

        case 'get_knowledge_categories': {
          const response = await KnowledgeCategoryService.getCategories();
          return {
            success: true,
            data: response.categories || [],
            total: response.total || 0
          };
        }

        case 'get_knowledge_category': {
          const category = await KnowledgeCategoryService.getCategoryById(args.category_id);
          return {
            success: true,
            data: category
          };
        }

        case 'get_lessons': {
          const response = await KnowledgeCategoryService.getPastLessons(
            args.category_slug,
            args.limit || 50,
            args.offset || 0
          );
          return {
            success: true,
            data: response.lessons || [],
            total: response.total || 0
          };
        }

        case 'get_lesson': {
          const lesson = await KnowledgeCategoryService.getLessonById(args.lesson_id);
          return {
            success: true,
            data: lesson
          };
        }

        case 'get_daily_notes': {
          // Get notes for the specified date
          const notesResponse = await NotesService.getUserNotes(userId, {
            start_date: args.date,
            end_date: args.date,
            limit: args.limit || 100
          });

          // Fetch full content for each note
          const notesWithContent = await Promise.all(
            (notesResponse.data || []).map(async (note) => {
              try {
                const noteWithBlocks = await NotesService.getNoteWithBlocks(note.note_id, userId);
                return {
                  ...note,
                  blocks: noteWithBlocks.blocks.map((block: any) => ({
                    block_id: block.block_id,
                    order: block.order,
                    content: block.content ? {
                      text_value: block.content.text_value,
                      content_type: block.content.content_type
                    } : null
                  }))
                };
              } catch (error) {
                console.error(`Error fetching blocks for note ${note.note_id}:`, error);
                return {
                  ...note,
                  blocks: []
                };
              }
            })
          );

          return {
            success: true,
            data: notesWithContent,
            count: notesWithContent.length
          };
        }

        default:
          return {
            success: false,
            error: `Unknown tool: ${toolName}`
          };
      }
    } catch (error: any) {
      console.error(`Error executing tool ${toolName}:`, error);
      return {
        success: false,
        error: error.message || `Failed to execute ${toolName}`
      };
    }
  }

  /**
   * Handle tool calls from Claude response
   */
  static async handleToolCalls(
    content: any[],
    userId: number
  ): Promise<ToolResultBlock[]> {
    const toolResults: ToolResultBlock[] = [];

    for (const block of content) {
      if (block.type === 'tool_use') {
        const toolUse = block as ToolUseBlock;
        console.log(`Executing tool: ${toolUse.name}`, toolUse.input);

        const result = await this.executeTool(toolUse.name, toolUse.input, userId);
        console.log(`Tool ${toolUse.name} result:`, result);

        // Format result for Claude
        const resultContent = result.success
          ? JSON.stringify(result.data)
          : JSON.stringify({ error: result.error });

        toolResults.push({
          type: 'tool_result',
          tool_use_id: toolUse.id,
          content: resultContent,
          is_error: !result.success
        });
      }
    }

    return toolResults;
  }

  /**
   * Get tool descriptions organized by domain for enabled tools
   */
  static getToolDescriptionsByDomain(enabledToolNames?: string[]): string {
    if (!enabledToolNames || enabledToolNames.length === 0) {
      return '';
    }

    // Define tool groups by domain
    const toolGroups: Record<string, Array<{ name: string; description: string }>> = {
      'Novels': [
        { name: 'get_user_novels', description: 'List all novels the user has created' },
        { name: 'get_novel', description: 'Get detailed information about a specific novel' },
        { name: 'get_chapter', description: 'Get the content of a specific chapter' },
        { name: 'update_novel', description: 'Update novel title and status' },
        { name: 'update_novel_metadata', description: 'Update novel metadata (genre, plots, characters, settings, themes, writing style, etc.)' }
      ],
      'Cashflow': [
        { name: 'get_cashflow_entries', description: 'Get cashflow entries with optional filters (date range, category)' },
        { name: 'get_cashflow_summary', description: 'Get summary statistics (spent/income/investment by period)' },
        { name: 'get_cashflow_categories', description: 'Get available cashflow categories' }
      ],
      'Tasks': [
        { name: 'create_task', description: 'Create a new task with title, optional description and tags' },
        { name: 'get_user_tasks', description: 'Get tasks with filters (status, due_date, assigned_date)' },
        { name: 'get_task_by_id', description: 'Get detailed information about a specific task' },
        { name: 'get_tasks_due_today', description: 'Get tasks due today' },
        { name: 'get_tasks_assigned_for_date', description: 'Get tasks assigned for a specific date' },
        { name: 'get_task_stats', description: 'Get task statistics (counts by status, overdue, etc.)' }
      ],
      'Habits': [
        { name: 'get_user_habits', description: 'Get habits with filters (category, is_active)' },
        { name: 'get_habit_by_id', description: 'Get detailed information about a specific habit' },
        { name: 'get_daily_completions', description: 'Get habit completions for a specific date' },
        { name: 'get_habit_stats', description: 'Get habit completion statistics' },
        { name: 'get_habit_streak', description: 'Get streak information for a habit' }
      ],
      'Goals': [
        { name: 'get_user_goals', description: 'Get goals with filters (status, category)' },
        { name: 'get_goal_by_id', description: 'Get detailed information about a specific goal' },
        { name: 'get_goal_progress_history', description: 'Get progress history for a goal' }
      ],
      'Knowledge': [
        { name: 'get_knowledge_categories', description: 'Get list of all knowledge categories' },
        { name: 'get_knowledge_category', description: 'Get detailed information about a specific knowledge category' },
        { name: 'get_lessons', description: 'Get lessons for a knowledge category' },
        { name: 'get_lesson', description: 'Get detailed information about a specific lesson with full content' }
      ],
      'Notes': [
        { name: 'get_daily_notes', description: 'Get notes for a specific date with full content (blocks)' }
      ]
    };

    let toolInfo = '';
    const enabledSet = new Set(enabledToolNames);

    // Build tool descriptions grouped by domain
    for (const [domain, tools] of Object.entries(toolGroups)) {
      const enabledToolsInDomain = tools.filter(tool => enabledSet.has(tool.name));
      
      if (enabledToolsInDomain.length > 0) {
        toolInfo += `${domain}:\n`;
        enabledToolsInDomain.forEach(tool => {
          toolInfo += `- ${tool.name}: ${tool.description}\n`;
        });
        toolInfo += '\n';
      }
    }

    return toolInfo;
  }

  /**
   * Get system prompt for a chat configuration
   */
  static async getSystemPrompt(config: ChatConfig): Promise<string> {
    let basePrompt = '';
    let enabledTools: string[] | undefined = undefined;

    if (config.characterId) {
      // Fetch character and use persona_info
      try {
        const response = await aiCharacterService.getCharacterById(config.characterId.toString());
        if (response.success && response.character) {
          const personaInfo = response.character.persona_info;
          if (personaInfo && typeof personaInfo === 'object') {
            // Extract enabled tools from persona_info
            enabledTools = personaInfo.enabled_tools;
            
            // Extract system prompt from persona_info
            if (personaInfo.system_prompt) {
              basePrompt = personaInfo.system_prompt;
            } else if (personaInfo.description) {
              basePrompt = personaInfo.description;
            } else {
              // If persona_info has other fields, create a prompt from them
              basePrompt = `You are ${response.character.name}. ${JSON.stringify(personaInfo)}`;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching character for system prompt:', error);
      }
    }

    // Use default system prompt for provider if no character prompt
    if (!basePrompt) {
      const provider = config.provider || 'claude';
      basePrompt = DEFAULT_SYSTEM_PROMPTS[provider];
    }

    // Add current date and time to system prompt
    const now = new Date();
    const currentDateTime = now.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
    const currentDateTimeISO = now.toISOString();
    basePrompt = `Current date and time: ${currentDateTime} (${currentDateTimeISO} UTC)

${basePrompt}`;

    // Add tool information to system prompt based on enabled tools
    const toolDescriptions = this.getToolDescriptionsByDomain(enabledTools);
    
    if (toolDescriptions) {
      basePrompt += `


You have access to ONLY the following tools for retrieving information about the user's data. Do NOT mention or claim access to any other tools that are not listed below. Use these tools proactively when they would help you provide better, more contextual assistance:

${toolDescriptions}
IMPORTANT: You can ONLY use the tools listed above. Do not mention or reference any other tools that are not in this list. When the user asks about their data, use the appropriate tools from the list above to retrieve relevant information before responding.`;
    } else if (config.characterId) {
      // Character has no enabled tools
      basePrompt += `


You do not have access to any tools. Do not mention or claim access to any tools. Respond to the user based on your knowledge and the conversation context only.`;
    } else {
      // Non-character config - include all tools (default behavior)
      const allToolNames = Object.keys(this.TOOL_REGISTRY);
      const allToolDescriptions = this.getToolDescriptionsByDomain(allToolNames);
      if (allToolDescriptions) {
        basePrompt += `


You have access to the following tools for retrieving information about the user's data. Use these tools proactively when they would help you provide better, more contextual assistance:

${allToolDescriptions}
When the user asks about their data, use the appropriate tools from the list above to retrieve relevant information before responding.`;
      }
    }

    console.log("Generated System Prompt:", basePrompt);
    return basePrompt;
  }

  /**
   * Get model name for a chat configuration
   */
  static async getModel(config: ChatConfig): Promise<string> {
    if (config.characterId) {
      // Fetch character and use its model
      try {
        const response = await aiCharacterService.getCharacterById(config.characterId.toString());
        if (response.success && response.character) {
          return response.character.model_name;
        }
      } catch (error) {
        console.error('Error fetching character for model:', error);
      }
    }

    // Use provided model or default for provider
    if (config.model) {
      return config.model;
    }
    const provider = config.provider || 'claude';
    return DEFAULT_MODELS[provider];
  }

  /**
   * Get provider for a chat configuration
   */
  static async getProvider(config: ChatConfig): Promise<'claude' | 'gemini' | 'gpt' | 'grok'> {
    if (config.characterId) {
      // Fetch character and use its provider
      try {
        const response = await aiCharacterService.getCharacterById(config.characterId.toString());
        if (response.success && response.character) {
          return response.character.model_provider;
        }
      } catch (error) {
        console.error('Error fetching character for provider:', error);
      }
    }

    // Use provided provider or default to claude
    return config.provider || 'claude';
  }

  /**
   * Send a chat message and get AI response
   */
  static async sendMessage(
    message: string,
    config: ChatConfig,
    previousMessages: ChatMessage[] = [],
    userId?: number
  ): Promise<ChatResponse> {
    // Get provider, model, and system prompt
    const provider = await this.getProvider(config);
    const model = await this.getModel(config);
    const systemPrompt = await this.getSystemPrompt(config);

    // Get tools (only for Claude provider, and only if userId is provided)
    let tools: any[] | undefined = undefined;
    if (provider === 'claude' && userId) {
      if (config.characterId) {
        // Fetch character and get enabled tools
        try {
          const response = await aiCharacterService.getCharacterById(config.characterId.toString());
          if (response.success && response.character) {
            const personaInfo = response.character.persona_info;
            const enabledTools = (personaInfo && typeof personaInfo === 'object' && personaInfo.enabled_tools) 
              ? personaInfo.enabled_tools 
              : undefined;
            tools = this.getToolsForCharacter(enabledTools);
          }
        } catch (error) {
          console.error('Error fetching character for tools:', error);
          // Do not fall back to any tools on error
          tools = undefined;
        }
      } else {
        // No character, use all tools
        tools = Object.values(this.TOOL_REGISTRY);
      }
    }

    // Convert ChatMessage[] to API format
    // Handle both string content and tool_use/tool_result blocks
    const apiMessages: any[] = previousMessages.map(msg => {
      // If message content is a string, use it directly
      if (typeof msg.content === 'string') {
        return {
          role: msg.role,
          content: msg.content
        };
      }
      // Otherwise, return as-is (might contain tool blocks)
      return {
        role: msg.role,
        content: msg.content
      };
    });

    // Add current user message
    apiMessages.push({
      role: 'user',
      content: message
    });

    // Maximum iterations for tool calling (prevent infinite loops)
    const MAX_TOOL_ITERATIONS = 5;
    let iteration = 0;
    let finalResponse: any = null;
    let accumulatedUsage = { input_tokens: 0, output_tokens: 0 };
    const executedToolCalls: ToolCall[] = [];

    while (iteration < MAX_TOOL_ITERATIONS) {
      // Prepare request body
      const requestBody: any = {
        provider,
        model,
        systemPrompt,
        messages: apiMessages,
        maxTokens: 2000,
        temperature: 0.7
      };

      // Add tools if available and not empty
      if (tools && Array.isArray(tools) && tools.length > 0) {
        requestBody.tools = tools;
      }

      // Call AI API
      const response = await apiCall('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to get AI response');
      }

      // Accumulate usage stats
      if (response.data.usage) {
        accumulatedUsage.input_tokens += response.data.usage.input_tokens || 0;
        accumulatedUsage.output_tokens += response.data.usage.output_tokens || 0;
      }

      finalResponse = response.data;

      // Check if AI wants to use tools
      if (response.data.stop_reason === 'tool_use' && response.data.content && userId) {
        // Extract tool_use blocks from content
        const toolUses: ToolUseBlock[] = response.data.content.filter((block: any) => block.type === 'tool_use');
        
        if (toolUses.length > 0) {
          // Execute tools
          const toolResults = await this.handleToolCalls(response.data.content, userId);

          // Correlate tool uses with results and store them
          toolUses.forEach(toolUse => {
            const result = toolResults.find(r => r.tool_use_id === toolUse.id);
            try {
              executedToolCalls.push({
                name: toolUse.name,
                input: toolUse.input,
                result: result ? JSON.parse(result.content as string) : undefined,
                isError: result ? result.is_error : true,
              });
            } catch (e) {
              // Handle cases where result.content is not valid JSON
              executedToolCalls.push({
                name: toolUse.name,
                input: toolUse.input,
                result: result ? result.content : undefined,
                isError: result ? result.is_error : true,
              });
            }
          });

          // Add assistant message with tool_use blocks
          apiMessages.push({
            role: 'assistant',
            content: response.data.content
          });

          // Add user message with tool results
          apiMessages.push({
            role: 'user',
            content: toolResults
          });

          iteration++;
          continue; // Loop to send tool results back to AI
        }
      }

      // No more tool calls needed, break the loop
      break;
    }

    if (!finalResponse) {
      throw new Error('Failed to get AI response after tool execution');
    }

    // Extract images if present
    const images = finalResponse.images?.map((img: any) => ({
      data: img.data,
      mimeType: img.mimeType || 'image/png',
      alt: 'AI Generated Image'
    }));

    // Extract text response from content blocks
    let responseText = '';
    if (finalResponse.content && Array.isArray(finalResponse.content)) {
      // Extract text from text blocks, skip tool_use blocks
      responseText = finalResponse.content
        .filter((block: any) => block.type === 'text')
        .map((block: any) => block.text || '')
        .join('');
    } else {
      responseText = finalResponse.response || '';
    }

    return {
      response: responseText,
      content: finalResponse.content || [],
      stop_reason: finalResponse.stop_reason || 'end_turn',
      usage: accumulatedUsage.input_tokens > 0 ? accumulatedUsage : finalResponse.usage,
      model: finalResponse.model || model,
      id: finalResponse.id,
      images,
      toolCalls: executedToolCalls.length > 0 ? executedToolCalls : undefined
    };
  }

  /**
   * Generate welcome message based on configuration
   */
  static async generateWelcomeMessage(config: ChatConfig, userName?: string): Promise<string> {
    if (config.characterId) {
      try {
        const response = await aiCharacterService.getCharacterById(config.characterId.toString());
        if (response.success && response.character) {
          const character = response.character;
          const personaInfo = character.persona_info;
          
          if (personaInfo && typeof personaInfo === 'object' && personaInfo.welcome_message) {
            return personaInfo.welcome_message;
          }
          
          return `Hello${userName ? ` ${userName}` : ''}! I'm ${character.name}. How can I help you today?`;
        }
      } catch (error) {
        console.error('Error fetching character for welcome message:', error);
      }
    }

    // Default welcome message
    const provider = config.provider || 'claude';
    const providerName = provider.charAt(0).toUpperCase() + provider.slice(1);
    return `Hello${userName ? ` ${userName}` : ''}! I'm your ${providerName} AI assistant. How can I help you today?`;
  }
}

export default GenericChatService;

